import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChevronDownRegular } from '@fluentui/react-icons';

import { envAccent, spacing } from '@/theme/tokens';
import { HUB_ENVS, HUB_ENV_FULL_LABEL, HUB_ENV_LABEL, type HubEnv } from '@/env/types';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} ${spacing.m}`,
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'background-color 120ms ease, border-color 120ms ease',
  },
  triggerHover: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground4,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  dot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
});

interface EnvBadgeProps {
  env: HubEnv;
  onChange: (env: HubEnv) => void;
}

export function EnvBadge({ env, onChange }: EnvBadgeProps) {
  const classes = useStyles();
  const accent = envAccent[env];

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={`${classes.trigger} ${classes.triggerHover}`}
          aria-label={`Environment: ${HUB_ENV_FULL_LABEL[env]}. Click to change.`}
          style={{ color: accent.fg, backgroundColor: accent.bg, borderColor: 'transparent' }}
        >
          <span className={classes.dot} style={{ backgroundColor: accent.fg }} aria-hidden />
          <Text size={200} weight="semibold" style={{ color: 'inherit' }}>
            {HUB_ENV_LABEL[env]}
          </Text>
          <ChevronDownRegular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {HUB_ENVS.map((option) => (
            <MenuItem key={option} onClick={() => { onChange(option); }}>
              <span
                className={classes.dot}
                style={{
                  backgroundColor: envAccent[option].fg,
                  marginRight: spacing.s,
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
                aria-hidden
              />
              {HUB_ENV_FULL_LABEL[option]}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
